import React, { useEffect, useState } from 'react';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { STORE_TIME_ZONES } from '../constants/storeTimeZones';
import { InlineMessage, LoadingState, SectionCard, StatusChip } from './ui';

export interface StoreTimezoneSetting {
  timezone: string;
  source: 'store' | 'address' | 'default';
}

export const STORE_TIMEZONE_QUERY_KEY = ['store-timezone'] as const;

const ENDPOINT = '/store/settings/timezone';

async function readBody(response: Response, fallback: string): Promise<StoreTimezoneSetting> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message || body?.message || fallback);
  }
  return body.data as StoreTimezoneSetting;
}

/**
 * Jordan sees the time zone the midnight price push uses (flag
 * `midnight_price_activation`) and corrects it when the address-derived guess is
 * wrong: a price dated for a day starts at 00:00 on that day in this zone.
 */
export const StoreTimezoneCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('midnight_price_activation');
  const [selected, setSelected] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: STORE_TIMEZONE_QUERY_KEY,
    queryFn: async () => readBody(await makeApiCall(ENDPOINT, { headers: await getAuthHeaders() }), t('store.timezone.loadError')),
    enabled,
  });

  useEffect(() => {
    if (data?.timezone) setSelected(data.timezone);
  }, [data?.timezone]);

  const save = useMutation({
    mutationFn: async (timezone: string) =>
      readBody(
        await makeApiCall(ENDPOINT, {
          method: 'PUT',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({ timezone }),
        }),
        t('store.timezone.saveError'),
      ),
    onSuccess: () => {
      showToast(t('store.timezone.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: STORE_TIMEZONE_QUERY_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  if (!enabled) return null;

  const options = data && !STORE_TIME_ZONES.some((z) => z.value === data.timezone)
    ? [{ value: data.timezone, label: data.timezone }, ...STORE_TIME_ZONES]
    : STORE_TIME_ZONES;

  return (
    <SectionCard title={t('store.timezone.title')} leading={<Schedule color="primary" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <InlineMessage severity="error">{t('store.timezone.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.timezone.intro')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <StatusChip
              tone={data.source === 'store' ? 'success' : data.source === 'address' ? 'info' : 'warning'}
              label={data.timezone}
            />
            <Typography variant="body2" color="text.secondary">
              {t(`store.timezone.${data.source === 'store' ? 'chosen' : data.source === 'address' ? 'detected' : 'default'}`)}
            </Typography>
          </Stack>
          <TextField
            select
            size="small"
            label={t('store.timezone.label')}
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
            sx={{ maxWidth: 360 }}
          >
            {options.map((zone) => (
              <MenuItem key={zone.value} value={zone.value}>
                {zone.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              disabled={!selected || (selected === data.timezone && data.source === 'store') || save.isPending}
              onClick={() => selected && save.mutate(selected)}
            >
              {t('common.save')}
            </Button>
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

export default StoreTimezoneCard;
